import React, { useEffect, useState } from "react";
import { Result, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Auth from "./components/auth/auth";
import AppShell from "./components/layout/app-shell";
import HealthClients from "./components/health/clients/health-clients";
import CheckDbPage from "./components/service/check-db-page";
import UserMonitors from "./components/service/monitor/user-monitors";
import ReportDesigns from "./components/service/reports/report-designs";
import ReportViewer from "./components/service/reports/report-viewer";
import SettingsPage, {
  initializeWorkplaceSettings
} from "./components/service/settings";
import SprZal from "./components/sprs/zal/spr-zal";
import SprTov from "./components/sprs/tov/spr-tov";
import SprKompl from "./components/sprs/kompl/spr-kompl";
import SprSotrs from "./components/sprs/sotr/spr-sotrs";
import SprTemplateNazn from "./components/sprs/template-nazn/spr-template-nazn";
import SprText from "./components/sprs/text/spr-text";
import SprHolidays from "./components/sprs/holidays/spr-holidays";
import SprKassa from "./components/sprs/kassa/spr-kassa";
import SprServis from "./components/sprs/servis/spr-servis";
import SprNumbers from "./components/sprs/number/spr-numbers";
import SprNumberCats from "./components/sprs/number-cat/spr-number-cats";
import SprNumberSprs from "./components/sprs/number-spr/spr-number-sprs";
import SprNumberPrices from "./components/sprs/number-price/spr-number-prices";
import SprPreds from "./components/sprs/pred/spr-preds";
import SprStatuses from "./components/sprs/status/spr-statuses";
import SprCards from "./components/sprs/cards/spr-cards";
import SprCardVids from "./components/sprs/cards/spr-card-vids";
import DocExps from "./components/doc/exp/doc-exps";
import DocPlats from "./components/doc/plat/doc-plats";
import WarehouseDocuments from "./components/warehouse/warehouse-documents";
import WarehouseRegister from "./components/warehouse/warehouse-register";
import WarehouseMovements from "./components/warehouse/warehouse-movements";
import WarehouseProductCard from "./components/warehouse/warehouse-product-card";
import BronCalendar from "./components/bron/bron-calendar";
import NutritionTemplates from "./components/nutrition/nutrition-templates";
import NutritionMap from "./components/nutrition/nutrition-map";
import NutritionRegistration from "./components/nutrition/nutrition-registration";
import NutritionHalls from "./components/nutrition/nutrition-halls";
import NutritionCost from "./components/nutrition/nutrition-cost";
import Home from "./components/home/home";
import { SectionHubPage } from "./components/hub/section-hub";
import About from "./components/help/about";
import Dialogs from "./components/dialogs/dialogs";
import Channels from "./components/dialogs/channels/channels";
import Agents from "./components/dialogs/agents/agents";
import { hasLicensedModule, PROGRAM_MODULES } from "./core/module-license";
import { APP_BRAND_TITLE, formatWindowTitle } from "./core/route-tab-meta";
import { serverFetch } from "./core/server";
import { setLicense, setLicenseError } from "./store/license-slice";
import { clearUserSession, setUserSession } from "./store/user-slice";

initializeWorkplaceSettings();

const LicensedRoute = ({ moduleNumber, children }) => {
  const license = useSelector((state) => state.license.data);
  return hasLicensedModule(license, moduleNumber) ? children : <Navigate to="/" replace />;
};

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const session = useSelector((state) => state.user.session);
  const licenseStatus = useSelector((state) => state.license.status);
  const licenseError = useSelector((state) => state.license.error);
  const [blockingSessionCheck, setBlockingSessionCheck] = useState(
    () => !!localStorage.getItem("sessionId")
  );

  useEffect(() => {
    let cancelled = false;
    serverFetch("license", { method: "GET" })
      .then((license) => {
        if (!cancelled) dispatch(setLicense(license));
      })
      .catch((error) => {
        if (!cancelled) {
          dispatch(setLicenseError(error.message || "Не удалось загрузить лицензию"));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (blockingSessionCheck) {
      document.title = APP_BRAND_TITLE;
      return;
    }
    if (!session) {
      document.title = `Вход — ${APP_BRAND_TITLE}`;
      return;
    }
    document.title = formatWindowTitle(location.pathname);
  }, [blockingSessionCheck, session, location.pathname]);

  useEffect(() => {
    if (licenseStatus !== "ready") return undefined;

    const sessionId = localStorage.getItem("sessionId");
    if (!sessionId) {
      setBlockingSessionCheck(false);
      return undefined;
    }

    let cancelled = false;
    serverFetch("auth/checksession", { method: "GET" })
      .then((sessionData) => {
        if (cancelled) return;
        dispatch(
          setUserSession({
            sessionId,
            userId: sessionData?.userId,
            name: sessionData?.userName,
            companyId: sessionData?.companyId,
            depId: sessionData?.depId,
            isAdmin: sessionData?.isAdmin,
            level: sessionData?.level,
            law: sessionData?.law,
            dolj: sessionData?.dolj
          })
        );
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem("sessionId");
        dispatch(clearUserSession());
      })
      .finally(() => {
        if (!cancelled) setBlockingSessionCheck(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, licenseStatus, location.pathname]);

  const onLogout = async () => {
    try {
      await serverFetch("auth/logout", { method: "POST" });
    } finally {
      localStorage.removeItem("sessionId");
      dispatch(clearUserSession());
    }
  };

  if (licenseStatus === "loading") {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin tip="Проверка лицензии..." />
      </div>
    );
  }

  if (licenseStatus === "error") {
    return (
      <Result
        status="error"
        title="Приложение заблокировано"
        subTitle={licenseError}
      />
    );
  }

  if (blockingSessionCheck) {
    return <div style={{ padding: 20 }}>Проверка сессии...</div>;
  }

  return !session ? (
    <Auth />
  ) : (
    <Routes>
      <Route path="/report" element={<ReportViewer />} />
      <Route element={<AppShell onLogout={onLogout} />}>
        <Route path="/" element={<Home />} />
        <Route path="/numbers" element={<Navigate to="/hub/numbers" replace />} />
        <Route path="/hub/:section" element={<SectionHubPage />} />
        <Route path="/help/about" element={<About />} />
        <Route path="/spr/zal" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.HEALTH}><SprZal /></LicensedRoute>} />
        <Route path="/spr/zal/:id" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.HEALTH}><SprZal /></LicensedRoute>} />
        <Route path="/spr/tov" element={<SprTov />} />
        <Route path="/spr/tov/:id" element={<SprTov />} />
        <Route path="/spr/kompl" element={<SprKompl />} />
        <Route path="/spr/sotr" element={<SprSotrs />} />
        <Route path="/spr/sotr/:id" element={<SprSotrs />} />
        <Route path="/spr/template-nazn" element={<SprTemplateNazn />} />
        <Route path="/spr/text" element={<SprText />} />
        <Route path="/spr/holidays" element={<SprHolidays />} />
        <Route path="/spr/kassa" element={<SprKassa />} />
        <Route path="/spr/servis" element={<SprServis />} />
        <Route path="/spr/number" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><SprNumbers /></LicensedRoute>} />
        <Route path="/spr/number/:id" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><SprNumbers /></LicensedRoute>} />
        <Route path="/spr/number-cat" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><SprNumberCats /></LicensedRoute>} />
        <Route path="/spr/number-cat/:id" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><SprNumberCats /></LicensedRoute>} />
        <Route path="/spr/number-spr" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><SprNumberSprs /></LicensedRoute>} />
        <Route path="/spr/number-spr/:id" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><SprNumberSprs /></LicensedRoute>} />
        <Route path="/number/price" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><SprNumberPrices /></LicensedRoute>} />
        <Route path="/number/price/:id" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><SprNumberPrices /></LicensedRoute>} />
        <Route path="/spr/pred" element={<SprPreds />} />
        <Route path="/spr/pred/:id" element={<SprPreds />} />
        <Route path="/spr/status" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><SprStatuses /></LicensedRoute>} />
        <Route path="/spr/status/:id" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><SprStatuses /></LicensedRoute>} />
        <Route path="/spr/cards" element={<SprCards />} />
        <Route path="/spr/card-vids" element={<SprCardVids />} />
        <Route path="/doc/exp" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><DocExps /></LicensedRoute>} />
        <Route path="/doc/exp/:id" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><DocExps /></LicensedRoute>} />
        <Route path="/doc/plat" element={<DocPlats />} />
        <Route path="/warehouse/documents" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.WAREHOUSE}><WarehouseDocuments /></LicensedRoute>} />
        <Route path="/warehouse/documents/:id" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.WAREHOUSE}><WarehouseDocuments /></LicensedRoute>} />
        <Route path="/warehouse/register" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.WAREHOUSE}><WarehouseRegister /></LicensedRoute>} />
        <Route path="/warehouse/movements" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.WAREHOUSE}><WarehouseMovements /></LicensedRoute>} />
        <Route path="/warehouse/product-card" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.WAREHOUSE}><WarehouseProductCard /></LicensedRoute>} />
        <Route path="/bron" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUMBER}><BronCalendar /></LicensedRoute>} />
        <Route path="/nutrition/templates" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUTRITION}><NutritionTemplates /></LicensedRoute>} />
        <Route path="/nutrition/map" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUTRITION}><NutritionMap /></LicensedRoute>} />
        <Route path="/nutrition/register" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUTRITION}><NutritionRegistration /></LicensedRoute>} />
        <Route path="/nutrition/tables" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUTRITION}><NutritionHalls /></LicensedRoute>} />
        <Route path="/nutrition/cost" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.NUTRITION}><NutritionCost /></LicensedRoute>} />
        <Route path="/health/clients" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.HEALTH}><HealthClients /></LicensedRoute>} />
        <Route path="/health/clients/:id" element={<LicensedRoute moduleNumber={PROGRAM_MODULES.HEALTH}><HealthClients /></LicensedRoute>} />
        <Route path="/dialogs" element={<Dialogs />} />
        <Route path="/dialogs/channels" element={<Channels />} />
        <Route path="/dialogs/agents" element={<Agents />} />
        <Route path="/service/check-db" element={<CheckDbPage />} />
        <Route path="/service/monitor" element={<UserMonitors />} />
        <Route path="/service/reports" element={<ReportDesigns />} />
        <Route path="/service/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

export default App;
